import ExcelJS from "exceljs";
import { columnNumberToExcelLetter } from "utils/excel.util";
import { ExcelImportProcess } from "types/process";

export async function exportImportResultsToExcel(
  process: ExcelImportProcess
): Promise<void> {
  const workbook = new ExcelJS.Workbook();

  workbook.creator = "Rules Authoring Tool";
  workbook.created = new Date();

  /* **** Created Properties Sheet **** */
  const createdSheet = workbook.addWorksheet("Created Properties");

  createdSheet.columns = [{ header: "Property Name", key: "name", width: 40 }];

  // Title row
  addWorksheetTitle(createdSheet);

  // Leave row 2 empty for spacing
  createdSheet.addRow({});

  // Header row (row 3)
  createdSheet.addRow({ name: "Property Name" });

  // Style header row & enable filtering
  styleHeaderRow(createdSheet, 3);

  // Data starts at row 4
  process.createdProperties.forEach((name) => {
    createdSheet.addRow({ name });
  });

  // Freeze title + header rows
  createdSheet.views = [{ state: "frozen", ySplit: 3 }];

  /* **** Replaced Properties Sheet **** */
  const replacedSheet = workbook.addWorksheet("Replaced Properties");

  replacedSheet.columns = [{ header: "Property Name", key: "name", width: 40 }];

  // Title row
  addWorksheetTitle(replacedSheet);

  // Leave row 2 empty for spacing
  replacedSheet.addRow({});

  // Header row (row 3)
  replacedSheet.addRow({ name: "Property Name" });

  // Style header row & enable filtering
  styleHeaderRow(replacedSheet, 3);

  // Data starts at row 4
  process.replacedProperties.forEach((name) => {
    replacedSheet.addRow({ name });
  });

  // Freeze title + header rows
  replacedSheet.views = [{ state: "frozen", ySplit: 3 }];

  /* **** Skipped Properties Sheet **** */
  const skippedSheet = workbook.addWorksheet("Skipped Properties");

  skippedSheet.columns = [
    { header: "Property Name", key: "name", width: 32 },
    { header: "Property ID", key: "id", width: 32 },
    { header: "Reason", key: "reason", width: 36 },
    { header: "Workbook", key: "workbook", width: 36 },
    { header: "Worksheet", key: "worksheet", width: 24 },
    { header: "Row", key: "row", width: 10 },
    { header: "Column", key: "column", width: 10 },
    { header: "Details", key: "errors", width: 60 },
  ];

  skippedSheet.getColumn("reason").alignment = {
    wrapText: true,
    vertical: "top",
  };

  skippedSheet.getColumn("errors").alignment = {
    wrapText: true,
    vertical: "top",
  };

  // Title row
  addWorksheetTitle(skippedSheet);

  // Spacer
  skippedSheet.addRow({});

  // Header row
  skippedSheet.addRow({
    name: "Property Name",
    id: "Property ID",
    reason: "Reason",
    workbook: "Workbook",
    worksheet: "Worksheet",
    row: "Row",
    column: "Column",
    errors: "Errors",
  });

  // Style header row
  styleHeaderRow(skippedSheet, 3);

  // Data rows
  process.skippedProperties.forEach((p) => {
    const row = skippedSheet.addRow({
      name: p.name,
      id: p.id,
      reason: p.reason,
      workbook: p.excelOrigin.workbook ?? "",
      worksheet: p.excelOrigin.worksheet ?? "",
      row: p.excelOrigin.row,
      column:
        p.excelOrigin.column != null
          ? columnNumberToExcelLetter(p.excelOrigin.column)
          : "",
      errors: p.errors.join("\n"),
    });

    row.getCell("errors").alignment = {
      wrapText: true,
      vertical: "top",
    };
  });

  // Freeze title + header
  skippedSheet.views = [{ state: "frozen", ySplit: 3 }];

  /* **** Download **** */
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  downloadBlob(blob, buildFileName());
}

function addWorksheetTitle(sheet: ExcelJS.Worksheet) {
  const titleCell = sheet.getCell("A1");
  titleCell.value = sheet.name;
  titleCell.font = { bold: true, size: 14 };
  titleCell.alignment = { horizontal: "left" };

  // Merge title across all columns
  sheet.mergeCells(1, 1, 1, sheet.columnCount);
}

function downloadBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function buildFileName(): string {
  const date = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
  return `Excel Import Results - ${date}.xlsx`;
}

function styleHeaderRow(sheet: ExcelJS.Worksheet, headerRowNumber: number) {
  const headerRow = sheet.getRow(headerRowNumber);

  headerRow.eachCell((cell) => {
    cell.font = { bold: true };
    cell.alignment = {
      vertical: "middle",
      horizontal: "center",
      wrapText: true,
    };
    cell.border = {
      bottom: { style: "medium" },
    };
  });

  sheet.autoFilter = {
    from: {
      row: headerRowNumber,
      column: 1,
    },
    to: {
      row: headerRowNumber,
      column: sheet.columnCount,
    },
  };

  headerRow.commit();
}
